import { useEffect, useRef } from "react";
import Dropzone from "dropzone";
import "dropzone/dist/dropzone.css";
import FaiBox from "../FaiBox";
import FaiDropzoneRoot from "./FaiDropzoneRoot";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface FaiDropzoneProps {
	note?: string;
	selectedImages?: File[];
	options?: Dropzone.DropzoneOptions;
	setSelectedImages?: (files: File[]) => void;
}

const FaiDropzone: React.FC<FaiDropzoneProps> = ({
	note = "",
	options = {},
	selectedImages = [],
	setSelectedImages = () => { },
}) => {
	const dropzoneRef = useRef<HTMLFormElement>(null);
	const dzInstanceRef = useRef<Dropzone | null>(null);
	const setSelectedImagesRef = useRef(setSelectedImages);

	// Update ref when prop changes
	useEffect(() => {
		setSelectedImagesRef.current = setSelectedImages;
	}, [setSelectedImages]);

	useEffect(() => {
		Dropzone.autoDiscover = false;

		function createDropzone() {
			if (dropzoneRef.current) {
				if (dzInstanceRef.current) {
					dzInstanceRef.current.destroy();
				}

				dzInstanceRef.current = new Dropzone(dropzoneRef.current, {
					...options,
				});

				// Auto-sync state when files change
				const updateState = () => {
					if (dzInstanceRef.current && setSelectedImagesRef.current) {
						setSelectedImagesRef.current(dzInstanceRef.current.files);
					}
				};

				dzInstanceRef.current.on("addedfile", (file) => {
					// If maxFiles is 1, replace existing files
					if (
						options.maxFiles === 1 &&
						dzInstanceRef.current &&
						dzInstanceRef.current.files.length > 1
					) {
						const otherFiles = dzInstanceRef.current.files.filter(
							(f) => f !== file
						);
						otherFiles.forEach((f) => dzInstanceRef.current?.removeFile(f));
					}
					updateState();
				});
				dzInstanceRef.current.on("removedfile", updateState);
			}
		}

		function removeDropzone() {
			if (dzInstanceRef.current) {
				dzInstanceRef.current.destroy();
				dzInstanceRef.current = null;
			}
		}

		createDropzone();

		return () => removeDropzone();
	}, [options]);

	if (selectedImages && selectedImages.length > 0) {
		return (
			<>
				{/* Previews Section */}
				{selectedImages.length > 0 && (
					<FaiBox display="flex" flexWrap="wrap" gap={2} mb={2}>
						{selectedImages.map((img, idx) => (
							<FaiBox
								key={idx}
								position="relative"
								className="border rounded-lg h-40 w-40"
							>
								<div
									style={{
										position: "absolute",
										top: "-10px",
										right: "-10px",
										cursor: "pointer",
										background: "var(--theme-color)",
										color: "white",
										borderRadius: "50%",
										width: "24px",
										height: "24px",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										boxShadow: "0 0 5px rgba(0,0,0,0.2)",
										zIndex: 1,
									}}
									onClick={() => {
										const updated = selectedImages.filter((_, i) => i !== idx);
										setSelectedImages(updated);
									}}
								>
									<span style={{ fontSize: "16px", fontWeight: "bold" }}>
										&times;
									</span>
								</div>
								<img
									src={(img as any).dataURL || URL.createObjectURL(img)}
									alt={`Preview ${idx}`}
									className="mx-auto max-h-36 mt-2"
									style={{ objectFit: "contain" }}
								/>
							</FaiBox>
						))}
					</FaiBox>
				)}

				{/* Dropzone Form */}
				<FaiDropzoneRoot
					component="form"
					action="/file-upload"
					ref={dropzoneRef}
					className="form-control dropzone"
					style={{ background: "#ebebeb" }}
				>
					<CloudUploadIcon style={{ fontSize: "48px", color: "#888" }} />
					<FaiBox className="dz-default dz-message">
						<FaiBox component="span" className="dz-button">
							Drag your file(s) or browse
						</FaiBox>
					</FaiBox>
					<FaiBox className="max-size-text">{note || ""}</FaiBox>
					<FaiBox className="fallback">
						<FaiBox
							component="input"
							name="file"
							type="file"
							multiple={options.uploadMultiple}
						/>
					</FaiBox>
				</FaiDropzoneRoot>
			</>
		);
	}

	return (
		<FaiDropzoneRoot
			component="form"
			action="/file-upload"
			ref={dropzoneRef}
			className="form-control dropzone"
			style={{ background: "#ebebeb" }}
		>
			<CloudUploadIcon style={{ fontSize: "48px", color: "#888" }} />
			<FaiBox className="dz-default dz-message">
				<FaiBox component="span" className="dz-button">
					Drag your file(s) or browse
				</FaiBox>
			</FaiBox>
			<FaiBox className="max-size-text">{note || ""}</FaiBox>
			<FaiBox className="fallback">
				<FaiBox
					component="input"
					name="file"
					type="file"
					multiple={options.uploadMultiple}
				/>
			</FaiBox>
		</FaiDropzoneRoot>
	);
};

export default FaiDropzone;
